// editor-api — versioned (v1) adapter between the editor and the Trinket backend
// (design D3). editor-core never talks to a controller route directly; every
// editor↔backend interaction goes through these three stable signatures, so a
// later (Phase 2) backend can be swapped in behind them without touching the
// editor:
//
//   loadProject(id)             -> Promise<{ source, mode, assetBaseUrl, assetManifest, files, assets }>
//   saveProject(id, files)      -> Promise<object>   (resolves persisted, rejects on failure)
//   saveAssets(id, files, assets)-> Promise<{ assetBaseUrl, assetManifest, assets }>
//   uploadAsset(file)           -> Promise<{ id, url, name, thumb }> (rejects, err.status, on non-OK)
//   assetUrl(ref)               -> string            (same-origin /runner-assets/<ref>/…)
//
// Implementation notes:
//   - loadProject reads the server-rendered runner:load payload already on the
//     page (the <script id="runner-load"> JSON) instead of a second round-trip.
//     It returns the active-file `source` AND the full `files` array the page
//     carries (the multi-file safety guard, design D3).
//   - saveProject PUTs to the existing trinket code endpoint with the session
//     cookie (same-origin). pgzero trinket.code is a JSON files array; the editor
//     passes the LIVE content of every file (editor-core.getFiles()), which is
//     serialised verbatim — so an edited sibling is saved too and untouched files
//     round-trip unchanged (design D4). A single-file trinket serialises to a lone
//     main.py array (identical to the pre-existing single-file behaviour).
//   - assetUrl mirrors the controller's /runner-assets/<trinketRef>/ proxy path.

export const VERSION = 'v1';

// The <script type="application/json"> block the controller renders the
// source + asset references into (shared with the standalone host glue).
const LOAD_PAYLOAD_ID = 'runner-load';

// Resolve the document at call time so the module imports cleanly under Node
// (unit tests stub globalThis.document) and only touches the DOM when used.
function getDocument() {
  if (typeof document !== 'undefined') {
    return document;
  }
  if (typeof globalThis !== 'undefined' && globalThis.document) {
    return globalThis.document;
  }
  return null;
}

function getFetch() {
  if (typeof fetch !== 'undefined') {
    return fetch;
  }
  if (typeof globalThis !== 'undefined' && globalThis.fetch) {
    return globalThis.fetch;
  }
  throw new Error('editor-api: fetch is not available in this environment');
}

// Read and normalise the server-staged runner:load payload from the page.
function readLoadPayload() {
  const doc = getDocument();
  const el = doc && doc.getElementById(LOAD_PAYLOAD_ID);
  if (!el || !el.textContent) {
    return { source: '', mode: '', assetBaseUrl: '', assetManifest: [], files: [], assets: [] };
  }
  let payload;
  try {
    payload = JSON.parse(el.textContent);
  } catch (e) {
    throw new Error('editor-api: could not parse the runner:load payload');
  }
  var normalised = {
    source: typeof payload.source === 'string' ? payload.source : '',
    // Per-language runner mode (clientside-multilang-editor): '' => pgzero default,
    // 'pygame' / 'python' select the additive runner paths. Passed to editor-core.
    mode: typeof payload.mode === 'string' ? payload.mode : '',
    assetBaseUrl: typeof payload.assetBaseUrl === 'string' ? payload.assetBaseUrl : '',
    assetManifest: Array.isArray(payload.assetManifest) ? payload.assetManifest : [],
    // The full trinket files array, carried so save can preserve sibling files (D3).
    files: Array.isArray(payload.files) ? payload.files : [],
    // The raw asset list ({id,url,name,thumbnail}) for the Assets panel (D4, task 4.3).
    assets: Array.isArray(payload.assets) ? payload.assets : []
  };
  // code-challenge-activity: only present (as OWN keys) for a Code Challenge's
  // student editor payload — omitted entirely (not even as `undefined` keys) for
  // every other surface, so the shape of every existing (non-challenge) payload
  // is byte-for-byte/deep-equal unaffected.
  if (payload.challengeAnswerKey) {
    normalised.challengeAnswerKey = payload.challengeAnswerKey;
  }
  if (typeof payload.challengeSeed === 'number') {
    normalised.challengeSeed = payload.challengeSeed;
  }
  // code-challenge-modify-extend (v2): the student's saved "What to add" self-ticks
  // (requirement indices) for a Modify / Extend challenge — present only on that
  // attempt's payload; absent (not even an undefined key) everywhere else.
  if (Array.isArray(payload.checklistTicks)) {
    normalised.checklistTicks = payload.checklistTicks;
  }
  // assignment-success-criteria ([4.3]): the teacher-authored success criteria for
  // an assignment surface — present only on an assignment payload (with criteria);
  // absent (not even an undefined key) everywhere else, so the plain-assignment
  // payload shape is unaffected. `checklistTicks` above is reused for the ticks.
  if (Array.isArray(payload.successCriteria)) {
    normalised.successCriteria = payload.successCriteria;
  }
  return normalised;
}

// Serialise the live files array for save (design D4). The editor passes
// editor-core.getFiles() — the live content of every tab, each carrying its
// original non-content fields. We write the array verbatim. As a safety net, a
// missing/empty array falls back to a single-file main.py array so a save can
// never persist an empty program (identical to the single-file behaviour).
function serializeFiles(files) {
  const list = Array.isArray(files)
    ? files.filter(function (f) { return f && typeof f.name === 'string'; })
    : [];
  if (list.length === 0) {
    return JSON.stringify([{ name: 'main.py', content: '' }]);
  }
  return JSON.stringify(list.map(function (f) {
    return Object.assign({}, f, {
      content: typeof f.content === 'string' ? f.content : ''
    });
  }));
}

// Load the trinket the editor should open. The controller already rendered the
// active source + full files array + asset payload into the page, so resolve from
// it (no extra request). `id` is accepted for signature stability — a later backend
// may fetch by id. The returned `files` array is what saveProject round-trips (D3).
export function loadProject(id) { // eslint-disable-line no-unused-vars
  try {
    return Promise.resolve(readLoadPayload());
  } catch (e) {
    return Promise.reject(e);
  }
}

// Persist the live files array to the existing trinket code endpoint with the
// session cookie. Owner-gating is enforced server-side (canEdit pre-handler); the
// Save control is only rendered for owners. `files` is editor-core.getFiles() —
// every tab's live content — serialised verbatim, so edited siblings are saved and
// untouched files round-trip unchanged (D4).
export function saveProject(id, files) {
  const f = getFetch();
  return f('/api/projects/' + encodeURIComponent(id) + '/code', {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code: serializeFiles(files) })
  }).then(function (res) {
    if (!res.ok) {
      throw new Error('editor-api: save failed (' + res.status + ')');
    }
    return res.json().catch(function () { return {}; });
  });
}

// The FormData constructor, resolved at call time so the module imports cleanly
// under Node (unit tests stub globalThis.FormData; uploadAsset only runs in-browser).
function getFormData() {
  if (typeof FormData !== 'undefined') { return FormData; }
  if (typeof globalThis !== 'undefined' && globalThis.FormData) { return globalThis.FormData; }
  throw new Error('editor-api: FormData is not available in this environment');
}

// Upload one image/sound to the user's asset store via the EXISTING upload route
// (clientside-media-assets, task 4.1). multipart/form-data, same-origin (session
// cookie), so isolation/COEP is preserved (no cross-origin subresource). The route
// replies { file: { id, url, name, mime, size, thumb } } (lib/controllers/users.js
// assetUpload -> File publicSpec, task 3.3); we normalise to the embedded-asset shape
// { id, url, name, thumb } the trinket.assets[] schema needs. Rejects with a typed
// Error (err.status set) on a non-OK status — e.g. 413 too large, 415 bad type, or
// 501 when features.assets is off — so the host can show an inline message.
export function uploadAsset(file) {
  const f = getFetch();
  const FormDataCtor = getFormData();
  const fd = new FormDataCtor();
  fd.append('file', file);
  // No explicit Content-Type: the browser sets the multipart boundary itself.
  return f('/api/users/assets', {
    method: 'POST',
    credentials: 'same-origin',
    body: fd
  }).then(function (res) {
    if (!res.ok) {
      const err = new Error('editor-api: asset upload failed (' + res.status + ')');
      err.status = res.status;
      throw err;
    }
    return res.json().catch(function () { return {}; });
  }).then(function (body) {
    const file = (body && body.file) || {};
    return {
      id: file.id,
      url: file.url,
      name: file.name,
      thumb: file.thumb
    };
  });
}

// Persist the trinket's live files AND its attached asset list via the existing /code
// route (clientside-media-assets, task 4.2). Same serialisation + owner-gating as
// saveProject; additionally sends `assets` (the embedded {id,url,name,thumbnail}
// list) which the route persists to trinket.assets[]. Resolves the server-rebuilt
// { assetBaseUrl, assetManifest, assets } so the caller can refresh the live manifest
// (editor-core.setAssets) — the server stays the single source of truth (design D3).
export function saveAssets(id, files, assets) {
  const f = getFetch();
  return f('/api/projects/' + encodeURIComponent(id) + '/code', {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      code: serializeFiles(files),
      assets: Array.isArray(assets) ? assets : []
    })
  }).then(function (res) {
    if (!res.ok) {
      throw new Error('editor-api: save failed (' + res.status + ')');
    }
    return res.json().catch(function () { return {}; });
  });
}

// Remix (fork) the trinket into the current user's library, carrying the live
// files. POSTs to the existing fork endpoint (session cookie, same-origin) with the
// full live files array (every tab's content, D4) and resolves the new trinket so
// the host can navigate to it. `_remix` marks it a remix (the server names it
// "<name> Remix"); `library=true` files it in the user's library. Used by the in-app
// editor's minimal top bar; owner-agnostic (any signed-in user may remix).
export function remixProject(id, files) {
  const f = getFetch();
  return f('/api/projects/' + encodeURIComponent(id) + '/forks?library=true', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code: serializeFiles(files), _remix: true })
  }).then(function (res) {
    if (!res.ok) {
      throw new Error('editor-api: remix failed (' + res.status + ')');
    }
    return res.json();
  });
}

// Same-origin asset URL for a trinket reference (the controller's /runner-assets/
// proxy). The runner resolves individual assets itself from assetBaseUrl in the
// load payload; this is exposed for hosts that need to build an asset URL directly.
export function assetUrl(ref) {
  return '/runner-assets/' + String(ref == null ? '' : ref).replace(/^\/+/, '');
}

// --- assignment / teacher-review chrome (clientside-teacher-review) ------------
// Versioned (v1) adapters over the EXISTING course feedback/submission/draft routes
// (no new backend besides the read-gating pre-handlers). The per-surface host chrome
// (review-chrome / assignment-chrome) wires these; editor-core never calls them
// (design D1 placement). Each takes the canonical getFiles() [{name,content}] and
// absorbs the per-route payload-shape differences internally.

// The course feedback/submission routes live under the
// /courses/{courseId}/lessons/{lessonId}/materials/{materialId} prefix. `ctx` carries
// those ids (passed by the host from the validated route/query params).
function courseMaterialBase(ctx) {
  ctx = ctx || {};
  return '/api/courses/' + encodeURIComponent(ctx.courseId) +
    '/lessons/' + encodeURIComponent(ctx.lessonId) +
    '/materials/' + encodeURIComponent(ctx.materialId);
}

// The submission/feedback routes validate `code` as a Joi.object() — the
// { code, assets, settings } envelope the controller stores onto the snapshot/revision
// trinket (NOT the bare array-STRING that PUT …/code takes). The inner `code` is the
// files-array JSON string (serializeFiles) so the saved trinket round-trips through the
// controller's buildClientsidePayload (which JSON-parses trinket.code into the files
// array) — every sibling file is preserved.
function serializeCodeEnvelope(files, assets, settings) {
  return {
    code     : serializeFiles(files),
    assets   : Array.isArray(assets) ? assets : [],
    settings : (settings && typeof settings === 'object') ? settings : {}
  };
}

// Resolve a fetch Promise: throw a typed error on a non-OK status, else parse JSON
// (tolerating an empty body). Shared by the adapters below.
function expectOk(label) {
  return function (res) {
    if (!res.ok) {
      throw new Error('editor-api: ' + label + ' failed (' + res.status + ')');
    }
    return res.json().catch(function () { return {}; });
  };
}

// Teacher: send feedback on a submission. POSTs the live revision files (as the
// `code` envelope) plus the feedback fields to the existing course feedback endpoint.
// `trinketId` is the SUBMISSION's id (the feedback attaches to it; the revision trinket
// is created server-side and linked into the feedback comment when includeRevision is
// on). opts = { comments, includeRevision, allowResubmit, assets, settings }.
export function sendFeedback(ctx, trinketId, files, opts) {
  opts = opts || {};
  const f = getFetch();
  return f(courseMaterialBase(ctx) + '/feedback', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      code            : serializeCodeEnvelope(files, opts.assets, opts.settings),
      // Server route/handler expect `projectId` (trinket->project rename); the value is the submission id.
      projectId       : trinketId,
      comments        : typeof opts.comments === 'string' ? opts.comments : '',
      includeRevision : !!opts.includeRevision,
      allowResubmit   : !!opts.allowResubmit
    })
  }).then(expectOk('sendFeedback'));
}

// Teacher: debounced autosave of the feedback comment draft (the host debounces
// ~500 ms, preserving the legacy behaviour). POSTs to the existing endpoint; does NOT
// finalise/send feedback.
export function autosaveFeedbackComments(trinketId, comments) {
  const f = getFetch();
  return f('/api/feedback-comments/' + encodeURIComponent(trinketId), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ comments: typeof comments === 'string' ? comments : '' })
  }).then(expectOk('autosaveFeedbackComments'));
}

// Teacher: autosave a changed submission option (include-revision / allow-resubmit).
// Only the booleans present on `opts` are sent (the route extendOwn's them onto
// submissionOpts).
export function autosaveSubmissionOpt(trinketId, opts) {
  opts = opts || {};
  const body = {};
  if (typeof opts.includeRevision === 'boolean') { body.includeRevision = opts.includeRevision; }
  if (typeof opts.allowResubmit === 'boolean') { body.allowResubmit = opts.allowResubmit; }
  const f = getFetch();
  return f('/api/submission-opt/' + encodeURIComponent(trinketId), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  }).then(expectOk('autosaveSubmissionOpt'));
}

// Teacher: accept a late submission. Wires to the EXISTING acceptSubmission endpoint
// (flips submissionState submittedLate -> submitted, re-enabling the feedback form);
// no new endpoint. `trinketId` is the submission's id.
export function acceptSubmission(ctx, trinketId) {
  const f = getFetch();
  return f(courseMaterialBase(ctx) + '/acceptSubmission', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ projectId: trinketId })
  }).then(expectOk('acceptSubmission'));
}

// Student: autosave the live files (every tab) to the student's Draft. Sends the
// files-array JSON STRING via the route's plain `code` field (Joi.string()), which the
// handler stores verbatim — round-tripping exactly like trinket.code, so sibling files
// are never dropped. (The route's alternative `zipCode` path JSON.parses into the
// String code field, which would corrupt a files array on save; the plain `code` path
// is the correct multi-file carrier.) assets/settings ride alongside when provided.
export function saveDraft(trinketId, files, assets, settings) {
  const f = getFetch();
  const body = { code: serializeFiles(files) };
  if (Array.isArray(assets)) { body.assets = assets; }
  if (settings && typeof settings === 'object') { body.settings = settings; }
  return f('/api/projects/' + encodeURIComponent(trinketId) + '/draft', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  }).then(expectOk('saveDraft'));
}

// Student: record a FLAGGED paste / large-insert on the working trinket
// (code-authenticity-snapshots, task 4.1). POSTs the record (pasted text, location,
// before/after full-file snapshots, char/line counts, and the running pasted/typed
// counters) to the flag-gated record route. The route 404s when the operator flag is
// off, so callers should only invoke this when capture is enabled; failures are
// non-fatal (capture must never disrupt the student's editing). `record` is a plain
// object matching the route payload.
export function recordAuthenticity(trinketId, record) {
  const f = getFetch();
  return f('/api/projects/' + encodeURIComponent(trinketId) + '/authenticity', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(record || {})
  }).then(expectOk('recordAuthenticity'));
}

// Teacher: fetch the material's ordered submission list (for in-page Prev/Next student
// nav). GETs the existing submissions endpoint; resolves the array the dashboard also
// reads (each entry carries lang + shortCode for navigation).
export function fetchSubmissions(ctx) {
  const f = getFetch();
  return f(courseMaterialBase(ctx) + '/submissions', {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' }
  }).then(expectOk('fetchSubmissions'));
}

// Student: submit the assignment. POSTs the live files (as the `code` envelope) to the
// existing submission endpoint, which creates the snapshot trinket and stamps
// submissionState/submittedOn. `parentTrinketId` is the student's working trinket (the
// submission's `_parent`). opts = { assets, settings, comments }.
export function submit(ctx, parentTrinketId, files, opts) {
  opts = opts || {};
  const f = getFetch();
  return f(courseMaterialBase(ctx) + '/submissions', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      code     : serializeCodeEnvelope(files, opts.assets, opts.settings),
      parent   : parentTrinketId,
      comments : typeof opts.comments === 'string' ? opts.comments : ''
    })
  }).then(expectOk('submit'));
}

// --- Code Challenge grading (code-challenge-activity) -------------------------

// Teacher: persist a recorded answer key (whole-key replace). `payload` =
// { recordedInputs, challengeSeed, checkpoints }.
export function saveChallengeAnswerKey(ctx, payload) {
  const f = getFetch();
  return f(courseMaterialBase(ctx) + '/challenge/answerKey', {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  }).then(expectOk('saveChallengeAnswerKey'));
}

// Student: record a Check attempt's outcome. `payload` = { parent, passed,
// runFailed, results }. Fires on every Check click, not only passing ones.
export function postChallengeCheck(ctx, payload) {
  const f = getFetch();
  return f(courseMaterialBase(ctx) + '/challengeCheck', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  }).then(expectOk('postChallengeCheck'));
}

// Student (code-challenge-modify-extend v2): persist the "What to add" self-ticks
// (an array of ticked requirement indices) on the student's OWN attempt. `parent`
// is the attempt working-copy Snippet id (same value the Check call posts). This is
// GUIDANCE progress only — it never touches submissionState/grading. Resolves on
// success; rejects on failure (the drawer keeps its optimistic state and logs).
export function saveChecklistTicks(ctx, parent, ticks) {
  const f = getFetch();
  return f(courseMaterialBase(ctx) + '/challengeChecklist', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    // keepalive lets a flush-on-close save complete after the page starts unloading.
    keepalive: true,
    body: JSON.stringify({ parent: parent, ticks: Array.isArray(ticks) ? ticks : [] })
  }).then(expectOk('saveChecklistTicks'));
}

// Student (assignment-success-criteria [4.3]): persist the success-criteria self-
// ticks on the student's OWN assignment attempt. Sibling of saveChecklistTicks above,
// posting to the dedicated `…/assignmentChecklist` route (keeps the challenge path
// byte-identical). `parent` is the assignment working-copy Snippet id. GUIDANCE only
// — never touches submissionState/grading.
export function saveAssignmentChecklistTicks(ctx, parent, ticks) {
  const f = getFetch();
  return f(courseMaterialBase(ctx) + '/assignmentChecklist', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    // keepalive lets a flush-on-close save complete after the page starts unloading.
    keepalive: true,
    body: JSON.stringify({ parent: parent, ticks: Array.isArray(ticks) ? ticks : [] })
  }).then(expectOk('saveAssignmentChecklistTicks'));
}

// --- teacher code suggestions (teacher-code-suggestions) ----------------------
// Additive v1 adapters over the new suggestion endpoints. The teacher sends a
// suggestion (live files + note) from the review surface; the student overview lists
// them (with the server-computed diff) and accepts one into the working draft. As with
// every adapter, editor-core never calls these — the per-surface host chrome does.

// Teacher: send a formative code SUGGESTION. POSTs the live files (as the `code`
// envelope, so sibling files are preserved) + an optional markdown note + the course
// context to the suggestion endpoint. `trinketId` is the review surface's subject (the
// working trinket, or a submission snapshot the backend walks to its working trinket).
// Resolves on 2xx with the created suggestion. Does NOT touch the submission/grading flow.
export function sendSuggestion(ctx, trinketId, files, note) {
  const f = getFetch();
  return f(courseMaterialBase(ctx) + '/suggestions', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      code      : serializeCodeEnvelope(files),
      projectId : trinketId,
      comments  : typeof note === 'string' ? note : ''
    })
  }).then(expectOk('sendSuggestion'));
}

// Student/staff: list a working trinket's suggestions (newest-first), each with the
// teacher's note (raw + server-rendered html), the revision reference, createdOn/status,
// and the server-computed side-by-side diff vs the student's current code. The response
// also carries `currentCode` — the server-resolved current code (draft-else-saved) for the
// plain "Your code" preview, since the student's edits live in the Draft, not working.code.
// GETs the trinket-scoped endpoint (session cookie, same-origin).
export function fetchSuggestions(trinketId) {
  const f = getFetch();
  return f('/api/projects/' + encodeURIComponent(trinketId) + '/suggestions', {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' }
  }).then(expectOk('fetchSuggestions'));
}

// Student (owner): accept a suggestion into the working draft. POSTs the working trinket
// id (in the path) + the suggestion id to apply; the backend replaces the working draft's
// code with that suggestion's revision and marks it accepted. Resolves on 2xx with the
// updated working state.
export function acceptSuggestion(trinketId, suggestionId) {
  const f = getFetch();
  return f('/api/projects/' + encodeURIComponent(trinketId) + '/suggestions/accept', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ suggestionId: suggestionId })
  }).then(expectOk('acceptSuggestion'));
}

// Student (owner): dismiss a suggestion WITHOUT applying it. POSTs the working trinket id
// (path) + the suggestion id; the backend marks it 'dismissed' (it drops out of pending).
export function dismissSuggestion(trinketId, suggestionId) {
  const f = getFetch();
  return f('/api/projects/' + encodeURIComponent(trinketId) + '/suggestions/dismiss', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ suggestionId: suggestionId })
  }).then(expectOk('dismissSuggestion'));
}

// --- editing-loop chrome (clientside-editor-parity) ---------------------------
// Thin adapters over EXISTING routes (no new backend). The host (main.js) wires
// these to buttons/modals; editor-core never calls them (design D1 placement).

// Canonical top-level client-side editor URL for a trinket (Share). PURE — no
// network, no DOM: returns the `/{lang}/{shortCode}` path (the isolated client-side
// editor, NOT the legacy /library/projects view). `mode` selects the route per
// language (clientside-multilang-editor): 'python' => /python, 'pygame' => /pygame,
// anything else (incl. '') => /pgzero. The host prefixes window.location.origin.
// `outputOnly` (optional, canvas-only-share) appends `?outputOnly=1`, the canvas-only
// share link: the same route renders the standalone run-only page instead of the
// editor. Additive — existing two-argument callers are unaffected.
export function shareLink(shortCode, mode, outputOnly) {
  var lang = mode === 'python' ? 'python' : mode === 'pygame' ? 'pygame' : 'pgzero';
  var path = '/' + lang + '/' + String(shortCode == null ? '' : shortCode).replace(/^\/+/, '');
  return outputOnly ? path + '?outputOnly=1' : path;
}

// Failure shape shared by the two project-update routes (inline-project-rename).
// `kind` is the discriminator a caller needs to say WHY a save did not happen:
//   'validation' — HTTP 200 whose body carries no `success` key. This repo's route
//                  parser SKIPS the handler and replies 200 + `{ field, flash }` on
//                  ANY Joi error (lib/util/routeParser.js), so an `res.ok`-only check
//                  reported success while the database kept the old value. Reachable
//                  in production: the remix path writes an UNCAPPED `name + ' Remix'`
//                  while the route caps names at 50 characters.
//   'http'       — a real non-OK status (401 expired session, 403 non-owner, 5xx).
//   'network'    — fetch itself rejected (offline, aborted).
// `detail` carries the server's own `flash.validation.{field}` message when there is
// one, so a surface can state the reason instead of shrugging.
function updateError(message, kind, extra) {
  var err = new Error(message);
  err.kind = kind;
  if (extra && extra.status) { err.status = extra.status; }
  if (extra && extra.detail) { err.detail = extra.detail; }
  return err;
}

// One PUT of a single project field. Resolves the parsed body ONLY when it says
// `success === true`; every other outcome rejects with the shape above.
function putProjectField(id, route, field, value, label) {
  const f = getFetch();
  var payload = {};
  payload[field] = typeof value === 'string' ? value : '';
  return f('/api/projects/' + encodeURIComponent(id) + '/' + route, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  }).then(function (res) {
    if (!res.ok) {
      throw updateError('editor-api: ' + label + ' update failed (' + res.status + ')',
        'http', { status: res.status });
    }
    return res.json().catch(function () { return {}; }).then(function (body) {
      if (!body || body.success !== true) {
        var flash = body && body.flash;
        var detail = flash && flash.validation && flash.validation[field];
        throw updateError('editor-api: ' + label + ' update rejected by the server',
          'validation', { detail: detail });
      }
      return body;
    });
  }, function (err) {
    // Two-argument .then: this handler sees ONLY a fetch-level rejection, never the
    // validation/http errors thrown above.
    throw updateError('editor-api: ' + label + ' update failed (network)',
      'network', { detail: err && err.message });
  });
}

// Update the trinket name via the existing PUT …/name route (owner-gated server
// side; the Settings control is owner-only). Resolves the persisted body; rejects
// on a non-OK status, a network failure, OR a benign 200 that persisted nothing.
export function updateName(id, name) {
  return putProjectField(id, 'name', 'name', name, 'name');
}

// Update the trinket description / instructions via the existing PUT …/description
// route. Same contract as updateName — the description route has the IDENTICAL
// benign-200 behaviour (test/lib/api/editor.js proves it on the wire), so a modal
// where one field tells the truth and the other does not would be worse than either.
export function updateDescription(id, description) {
  return putProjectField(id, 'description', 'description', description, 'description');
}

// The Blob constructor / URL API, resolved at call time so the module imports
// cleanly under Node (the single-file download path only runs in the browser).
function getBlobCtor() {
  if (typeof Blob !== 'undefined') { return Blob; }
  if (typeof globalThis !== 'undefined' && globalThis.Blob) { return globalThis.Blob; }
  throw new Error('editor-api: Blob is not available in this environment');
}

function getURLApi() {
  if (typeof URL !== 'undefined' && URL.createObjectURL) { return URL; }
  if (typeof globalThis !== 'undefined' && globalThis.URL && globalThis.URL.createObjectURL) {
    return globalThis.URL;
  }
  throw new Error('editor-api: URL.createObjectURL is not available in this environment');
}

// Trigger a browser download of a Blob under `filename` via a transient anchor.
// Same-origin object URL only (no cross-origin subresource), so isolation/COEP is
// unaffected.
function triggerBlobDownload(blob, filename) {
  const doc = getDocument();
  if (!doc || !doc.createElement) {
    throw new Error('editor-api: document is not available for download');
  }
  const urlApi = getURLApi();
  const href = urlApi.createObjectURL(blob);
  const a = doc.createElement('a');
  a.href = href;
  a.download = filename;
  // Some browsers only honour a programmatic click on an in-document element.
  if (doc.body && doc.body.appendChild) { doc.body.appendChild(a); }
  a.click();
  if (a.parentNode && a.parentNode.removeChild) { a.parentNode.removeChild(a); }
  if (urlApi.revokeObjectURL) { urlApi.revokeObjectURL(href); }
}

// Build the zip endpoint's `assets` array ([{ name, url }]) from the trinket's
// asset base + manifest (relpaths). `name` keeps the in-zip path; `url` is the
// same-origin /runner-assets/ URL the server fetches by basename.
function buildDownloadAssets(meta) {
  const base = (meta && typeof meta.assetBaseUrl === 'string') ? meta.assetBaseUrl : '';
  const manifest = (meta && Array.isArray(meta.assetManifest)) ? meta.assetManifest : [];
  if (!base || manifest.length === 0) { return []; }
  const trimmedBase = base.replace(/\/+$/, '');
  return manifest.map(function (rel) {
    const relPath = String(rel == null ? '' : rel).replace(/^\/+/, '');
    return { name: relPath, url: trimmedBase + '/' + relPath };
  });
}

// Server's filename sanitiser (mirrors downloadPostedZip) so the anchor's
// download name matches the Content-Disposition the endpoint sets.
function safeZipName(filename) {
  const cleaned = String(filename == null ? '' : filename)
    .replace(/[^a-zA-Z0-9_\-\s]/g, '').substring(0, 100).trim();
  return cleaned || 'trinket-download';
}

// Download the trinket's LIVE content (design D2). `files` is editor-core.getFiles()
// ([{ name, content }]); `meta` carries { filename, assetBaseUrl, assetManifest }.
//   - Single code file, NO assets  -> produce the file client-side (a Blob named
//     after the file, e.g. main.py). NO backend request — fully COEP-safe.
//   - Multiple files, or any assets -> POST the live files (as a JSON object-map
//     STRING) + assets to the existing /api/projects/download and download the zip.
// Resolves a small descriptor of what was produced; rejects on a non-OK zip status.
export function downloadProject(meta, files) {
  meta = meta || {};
  const list = Array.isArray(files)
    ? files.filter(function (f) { return f && typeof f.name === 'string'; })
    : [];
  const assets = buildDownloadAssets(meta);

  if (list.length === 1 && assets.length === 0) {
    const only = list[0];
    const BlobCtor = getBlobCtor();
    const blob = new BlobCtor([typeof only.content === 'string' ? only.content : ''],
      { type: 'text/plain' });
    triggerBlobDownload(blob, only.name);
    return Promise.resolve({ kind: 'file', name: only.name });
  }

  // The download route validates files/assets as JSON STRINGS, so stringify both.
  const filesMap = {};
  list.forEach(function (f) {
    filesMap[f.name] = typeof f.content === 'string' ? f.content : '';
  });
  const filename = (typeof meta.filename === 'string' && meta.filename)
    ? meta.filename : 'trinket-download';
  const f = getFetch();
  return f('/api/projects/download', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      files: JSON.stringify(filesMap),
      assets: JSON.stringify(assets),
      filename: filename
    })
  }).then(function (res) {
    if (!res.ok) {
      throw new Error('editor-api: download failed (' + res.status + ')');
    }
    return res.blob();
  }).then(function (blob) {
    triggerBlobDownload(blob, safeZipName(filename) + '.zip');
    return { kind: 'zip', name: filename };
  });
}
